import React, { useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import Container from "../Container";
import { DesktopOnlyWrapper, MobileOnlyWrapper } from "../Wrapper";
import { BodySemiboldStyle, CaptionSemiboldStyle } from "../Typography";
import FooterLinks from "./footer-links";
import FooterLogo from "./images/footer-logo.svg";

import Modal from "../Pardot/Modal";
import PardotForm from "../Pardot/PardotForm";

import theme from "../../styles/theme";

const MainFooter = styled.footer`
  background: ${() => theme.colors.green5};
  color: ${() => theme.colors.gray1};
  padding: 32px 5% 48px;
  ${props => theme.media.tabletDown`
    padding: 32px 0 48px 0;
  `};
  ${props => theme.media.tabletUp`
    flex-direction: row;
    padding: 100px 0;
    flex-wrap: wrap;
  `};
`;

const FooterContainer = styled(Container)`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  ${props => theme.media.tabletUp`
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: wrap;
  `};
`;

const Logo = styled.img`
  width: 50%;
  margin-bottom: 16px;
  ${() => theme.media.desktopUp`
    margin-bottom: 0;
    width: 275px;
  `};
`;

const SocialColumn = styled.div`
  max-width: 400px;
  ${() => theme.media.desktopUp`
    display:flex;
    flex-direction: column;
    justify-content: space-between;
  `};
`;

const Copyright = styled.div`
  font-family: 'Graphik', 'Graphik Web', 'Helvetica Neue',Helvetica,Arial,sans-serif;
  font-size: 12px;
  margin-top: 20px;
`

const LinksColumn = styled.div`
  width: 100%;
  ${() => theme.media.desktopUp`
    width: 50%;
  `};
`;

const LinkRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  flex-direction: row;
  ${() => theme.media.desktopUp`
    flex-direction: row;
  `}
  ${() => theme.media.tabletDown`
    margin-bottom: 30px;
    margin-top: 30px;
    &:first-child {
      margin-bottom: 60px;
    }
    > a {
      width: 44%;
      display: block;
    }
  `}
`;

const LinkColumn = styled.div`
  width: 100%;
  font-family: 'Graphik', 'Graphik Web', 'Helvetica Neue',Helvetica,Arial,sans-serif;
  font-size: 14px;
  ${() => theme.media.tabletDown`
    width: 44%;
  `};
  ${() => theme.media.desktopUp`
    margin-top: 32px;
    width: calc(100% / 3);
    padding-right: 40px;
  `};
`;

const PrimaryTextStyle = css`
  ${BodySemiboldStyle};
  color: ${() => theme.colors.green2};
  font-family: 'Graphik', 'Graphik Web', 'Helvetica Neue',Helvetica,Arial,sans-serif;
  font-size: 18px;
`;

const PrimaryLink = styled.a`
  ${PrimaryTextStyle};
  text-decoration: none;
  cursor: pointer;
  margin: 16px 0;
  ${() => theme.media.desktopUp`
    margin: 0;
    width: calc(100% / 3);
    padding-right: 50px;
  `};
  &:hover {
    color: ${() => theme.colors.green3};
  }
`;

const LinkColumnHeader = styled.p`
  ${PrimaryTextStyle};
  padding-bottom: 8px;
  border-bottom: ${() => theme.colors.green2} solid 1px;
  margin-bottom: 36px;  
  ${() => theme.media.desktopUp`
    margin-bottom: 56px;
  `};
`;

const GroupHeading = styled.p`
  ${CaptionSemiboldStyle};
  color: ${() => theme.colors.green3};
  margin-top: 32px;
  margin-bottom: 32px;
`;

const SocialLinkWrapper = styled.div`
  margin-top: 32px;
  ${() => theme.media.desktopUp`
    margin-top: 0;
  `};
`;

const SocialLink = styled.a`
  margin-right: 16px;
`;

const FooterLink = styled.a`
  ${CaptionSemiboldStyle}
  display: block;
  cursor: pointer;
  color: ${() => theme.colors.green1};
  margin: 8px 0;
  text-decoration: none;
  font-family: 'Graphik', 'Graphik Web', 'Helvetica Neue',Helvetica,Arial,sans-serif;
  ${props => theme.media.tabletUp`
    margin: 12px 0;
  `};
  &:hover,
  &:active,
  &:focus {
    color: ${() => theme.colors.green2};
  }
`;

const Footer = ({ className }) => {
  const trialModalRef = useRef(null);


  useEffect(() => {
    const signupLinks = document.querySelectorAll('a[href^="https://app.mode.com/signup"]');

    function linkClicked(e) {
      e.preventDefault();
      trialModalRef.current.openModal();
    }

    signupLinks.forEach(link => {
      link.addEventListener('click', linkClicked)
    });

    return () => {
      signupLinks.forEach(link => {
        link.removeEventListener('click', linkClicked);
      });
    }
  }, []);

  const SocialLinkSection = (
    <SocialLinkWrapper>
      {FooterLinks.socialLinks.map(link => (
        <SocialLink key={link.title} href={link.url}>
          <img src={link.icon} alt={link.title} />
        </SocialLink>
      ))}
      {FooterLinks.legalLinks.map(link => (
        <FooterLink key={link.label} href={link.url}>
          {link.label}
        </FooterLink>
      ))}
      <Copyright>© Mode Analytics, Inc. {new Date().getFullYear()}</Copyright>
    </SocialLinkWrapper>
  );
  return (
    <div className={className}>
      <MainFooter>
        <FooterContainer>
          <MobileOnlyWrapper>
            <Logo src={FooterLogo} />
          </MobileOnlyWrapper>
          <DesktopOnlyWrapper>
            <SocialColumn>
              <Logo src={FooterLogo} />
              {SocialLinkSection}
            </SocialColumn>
          </DesktopOnlyWrapper>
          <LinksColumn>
            <LinkRow>
              {FooterLinks.primaryLinks.map(link => (
                <PrimaryLink key={link.label} href={link.url}>
                  {link.label}
                </PrimaryLink>
              ))}
            </LinkRow>
            <LinkRow>
              {FooterLinks.columns.map(column => (
                <LinkColumn key={column.title}>
                  <LinkColumnHeader>{column.title}</LinkColumnHeader>
                  {column.groups.map((group, index) => (
                    <div key={index}>
                      {group.heading && (
                        <GroupHeading>{group.heading}</GroupHeading>
                      )}
                      {group.links.map(link => (
                        <FooterLink
                          key={link.label}
                          href={link.url}
                          beta={link.beta}
                        >
                          {link.label}
                        </FooterLink>
                      ))}
                    </div>
                  ))}
                </LinkColumn>
              ))}
            </LinkRow>
          </LinksColumn>
          <MobileOnlyWrapper>{SocialLinkSection}</MobileOnlyWrapper>
        </FooterContainer>
      </MainFooter>
      <Modal style={{marginTop: '-300px'}} ref={trialModalRef}>
        <h2 style={{fontFamily: 'Grenette', fontSize: '36px', fontWeight: 400, letterSpacing: '-1.12px', lineHeight: '47px', marginBottom: '10px'}}>Try Mode for free</h2>
        <PardotForm config={JSON.stringify({
          id: "mode-form-signup-universal",
          embedContext: "page",
          embedStyle: { "maxWidth": "100%", "width": "380px" },
          strings: { "button_text": "Continue" },
          formUrl: "https://go.thoughtspot.com/l/710713/2024-03-05/btbvm",
          modeSignupForm: true,
          formValidationSettings: { "email": ["role_blacklist"] }
        })} />
      </Modal>
    </div>
  );
};

export default Footer;
